import React, { useMemo, useState, useEffect } from "react";
import {
    Table,
    Card,
    Alert,
    Placeholder,
    Modal,
    Button,
    Form,
    Spinner,
} from "react-bootstrap";
import { SearchBar, FilterField } from "@core/components/SearchBar";
import NoResult from "@core/components/NoResult";
import { useSearchParams } from "react-router-dom";
import { useDebounce } from "@core/hooks/useDebounce";
import { useSearchUrlSync } from "@core/hooks/useSearchUrlSync";
import { InvitationsTableProps } from "@features/invitations/types/invitationsTableProps";
import { useInvitations } from "@features/admin/invitations/hooks/useInvitations";
import { useInviteActions } from "@features/admin/invitations/hooks/useInviteActions";
import { useSelection } from "@features/invitations/hooks/useSelection";
import PaginationBar from "@core/components/PaginationBar";
import TablePaginationFooter, { getPaginationRange } from "@core/components/TablePaginationFooter";
import TableActionButton from "@core/components/TableActionButton";
import TableActions from "@core/components/TableActions";
import EntityCard from "@core/components/EntityCard";
import StatusBadge from "../../../invitations/components/StatusBadge";
import { getEffectiveScopeFromParams } from "@features/admin/invitations/utils/getEffectiveScopeFromParams";
import { InvitationStatus } from "@features/invitations/types/invitation";
import BulkActionsBar from "./BulkActionsBar";
import { formateDate } from "@infrastructure/api/utils/formateDateTime";
import { FiChevronRight, FiChevronDown } from "react-icons/fi";
import { getScopeFromValue } from "@features/invitations/utils/getScopeFromParam";

/**
 * Represents the state for confirming invitation actions.
 * 
 * - `single`: Used when confirming a single invitation, identified by its `id` and optionally its `email`.
 * - `bulk`: Used when confirming multiple invitations at once, identified by an array of `ids`.
 */
type ConfirmState =
    | { kind: "single"; id: number; email?: string }
    | { kind: "bulk"; ids: number[] };


const STATUS_OPTIONS = [
    { value: "all", label: "All Statuses" },
    { value: "pending", label: "Pending" },
    { value: "accepted", label: "Accepted" },
    { value: "revoked", label: "Revoked" },
    { value: "declined", label: "Declined" },
] as const;

const INVITATION_FILTER_FIELDS: FilterField[] = [
    {
        name: 'status',
        label: 'Status',
        type: 'select',
        includeEmptyOption: false,
        colWidth: 4,
        options: STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
    },
    { name: 'email',          label: 'Email',           type: 'text',   placeholder: 'Filter by email', colWidth: 4 },
    { name: 'role',           label: 'Role',            type: 'text',   placeholder: 'Filter by role / profile', colWidth: 4 },
    { name: 'departmentName', label: 'Department',      type: 'text',   placeholder: 'Filter by department name', colWidth: 4 },
    { name: 'trn',            label: 'TRN',             type: 'text',   placeholder: 'Filter by TRN', colWidth: 4 },
];

/**
 * Renders a table displaying invitations with filtering, selection, bulk actions, and pagination.
 *
 * @component
 * @param {InvitationsTableProps} props - The component props.
 * @param {string} [props.scope] - The scope of the invitations (e.g., organization, project).
 * @param {string | number} [props.scopeRefId] - The reference ID for the scope.
 * @param {boolean} [props.showPagination=false] - Whether to show the pagination bar.
 *
 * @returns {JSX.Element} The rendered invitations table component.
 *
 * @remarks
 * - Allows filtering invitations by status.
 * - Supports selecting multiple invitations for bulk actions (resend, cancel).
 * - Handles loading and empty states.
 * - Displays a modal for confirming single or bulk cancellation of invitations.
 * - Requires `useInvitations`, `useSelection`, and `useInviteActions` hooks for data and actions.
 * - Expects `STATUS_OPTIONS`, `formateDate`, `StatusBadge`, `BulkActionsBar`, and `PaginationBar` to be available in scope.
 */
const InvitationsTable: React.FC<InvitationsTableProps> = ({
    scope,
    scopeRefId,
    showPagination = false,
    leadingControl,
    reloadSignal,
}) => {
    const [searchParams] = useSearchParams();
    const { effectiveScope, effectiveScopeRefId } = getEffectiveScopeFromParams(
        searchParams,
        scope ?? "",
        scopeRefId ?? undefined
    );

    const dynamicScope = useMemo(() => getScopeFromValue(effectiveScope), [effectiveScope]);

    const [page, setPage] = useState(() => Number(searchParams.get('page')) || 1);
    const [freetext, setFreetext] = useState(() => searchParams.get('q') ?? '');
    const [filters, setFilters] = useState<Record<string, any>>(() => {
        const init: Record<string, any> = { status: searchParams.get('status') ?? 'pending' };
        const email = searchParams.get('email');
        const role = searchParams.get('role');
        const trn = searchParams.get('trn');
        const dept = searchParams.get('departmentName');
        if (email) init.email = email;
        if (role) init.role = role;
        if (trn) init.trn = trn;
        if (dept) init.departmentName = dept;
        return init;
    });

    const debouncedFreetext = useDebounce(freetext, 300);

    const status = (filters.status ?? 'pending') as "pending" | "accepted" | "revoked" | "declined" | "all";

    // Reset page when filters, freetext, or the viewed scope change
    useEffect(() => { setPage(1); }, [filters, debouncedFreetext, effectiveScopeRefId]);

    useSearchUrlSync({ query: debouncedFreetext, filters, page });

    const { current_page, data, last_page, per_page, total, loading, reload } = useInvitations({
        all: status === "all" ? "true" : "false",
        scope: dynamicScope,
        scope_ref_id: Number(effectiveScopeRefId),
        page,
        status: status === "all" ? "" : status,
        freetext: debouncedFreetext || undefined,
        email: filters.email || undefined,
        role: filters.role || undefined,
        trn: filters.trn || undefined,
        departmentName: filters.departmentName || undefined,
    });

    // External reload trigger (e.g. after an invite is sent into the currently-viewed scope).
    useEffect(() => {
        if (reloadSignal !== undefined && reloadSignal > 0) reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reloadSignal]);

    const totalPages = Math.max(1, Math.ceil(total / (per_page ?? 20)));
    const pageNumber = current_page ?? page;
    const { start, end } = getPaginationRange({
        page: pageNumber,
        perPage: per_page,
        total,
        currentCount: data.length,
    });

    const { selected, allSelectedOnPage, toggleAll, toggleOne, clear } = useSelection(data);
    const selectedHas = (id: number) => selected.has(id.toString());

    // Row expansion (independent multi-open) — secondary fields live in a detail row.
    const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
    const toggleExpanded = (id: number) =>
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    const { doCancel, doResend } = useInviteActions();

    // bulk actions
    const resendMany = async () => {
        await Promise.all(Array.from(selected).map((invitation_id) => doResend(Number(invitation_id), { scope: dynamicScope, scope_ref_id: Number(effectiveScopeRefId) })));
        clear();
        reload();
    };

    // confirm modal state
    const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
    const [confirming, setConfirming] = useState(false);

    // open single confirm modal
    const openConfirmSingle = (id: number, email?: string) =>
        setConfirmState({ kind: "single", id, email });

    // open bulk confirm modal
    const openConfirmBulk = () => {
        if (selected.size === 0) return;
        const ids = Array.from(selected).map((s) => Number(s));
        setConfirmState({ kind: "bulk", ids });
    };

    // close modal if not confirming
    const closeConfirm = () => {
        if (!confirming) setConfirmState(null);
    };

    // Handles single or bulk invitation cancellation
    const confirmCancel = async () => {
        // guard
        if (!confirmState) return;

        try {
            // set loading state
            setConfirming(true);

            // perform action based on kind
            if (confirmState.kind === "single") {
                // single
                await doCancel(confirmState.id, {
                    scope: dynamicScope,
                    scope_ref_id: Number(effectiveScopeRefId),
                });
            } else {
                // bulk
                await Promise.all(
                    confirmState.ids.map((id) =>
                        doCancel(id, {
                            scope: dynamicScope,
                            scope_ref_id: Number(effectiveScopeRefId),
                        })
                    )
                );
                clear(); // clear selection after bulk cancel
            }

            // close modal and reload data
            setConfirmState(null);
            reload();
        } finally {
            // reset loading state
            setConfirming(false);
        }
    };

    const activeFilterCount = useMemo(() => {
        let n = 0;
        if (status !== 'pending') n++;
        if (filters.email) n++;
        if (filters.role) n++;
        if (filters.departmentName) n++;
        if (filters.trn) n++;
        return n;
    }, [status, filters]);

    // Action set shared by the desktop row and the mobile card.
    const renderInviteActions = (r: (typeof data)[number]) => {
        const id = Number(r.invitation_id);
        const isPending = r.invite_status_name?.toLowerCase() === "pending";
        const canCancel = r.invite_status_id === 100;
        return (
            <TableActions aria-label={`Actions for invitation ${r.invitee_email}`}>
                <TableActionButton
                    icon="send"
                    label="Resend"
                    primary
                    variant="outline-primary"
                    disabled={!isPending}
                    onClick={() => isPending && doResend(r.invitation_id, { scope: dynamicScope, scope_ref_id: Number(effectiveScopeRefId) })}
                    data-testid={`resend-btn-${id}`}
                />
                <TableActionButton
                    icon="x-circle"
                    label="Cancel"
                    variant="outline-danger"
                    disabled={!canCancel}
                    onClick={() => canCancel && openConfirmSingle(id, r.invitee_email)}
                    data-testid={`cancel-btn-${id}`}
                />
            </TableActions>
        );
    };

    // guard
    if (!effectiveScope || !effectiveScopeRefId) {
        return (
            <Alert variant="danger" className="my-4">
                Error: Missing required parameters. <strong>scope</strong> and <strong>scopeRefId</strong>.
            </Alert>
        );
    }

    return (
        <div data-testid="invitations-table-root">
            {selected.size > 1 && (
                <BulkActionsBar
                    count={selected.size}
                    onCancelMany={openConfirmBulk}
                    onResendMany={resendMany}
                    onClearSelection={clear}
                    data-testid="bulk-actions-bar"
                />
            )}

            <div className="ntcog-list-search-section">
                <SearchBar
                    placeholder="Search invitations…"
                    filterFields={INVITATION_FILTER_FIELDS}
                    initialQuery={freetext}
                    initialFilters={filters}
                    onQueryChange={(q) => { setFreetext(q); }}
                    onFiltersApply={(f) => { setFilters({ status: 'pending', ...f }); setPage(1); }}
                    onClear={() => { setFreetext(''); setFilters({ status: 'pending' }); setPage(1); }}
                    loading={loading}
                    activeFilterCount={activeFilterCount}
                    leadingControl={leadingControl}
                    testId="invitations-search"
                />
            </div>

            <Card className="shadow-sm ntcog-card ntcog-list-table-card" data-testid="invitations-table-card">
                <Card.Body className="p-0">
                    <div className="px-3 pb-3 d-none d-md-block">
                        <Table
                            responsive
                            hover
                            size="sm"
                            className="mb-0 align-middle table-striped ntcog-table"
                            data-testid="invitations-table"
                        >
                            <thead className="table-light">
                                <tr className="small text-muted">
                                    <th style={{ width: 36 }} aria-label="Expand row" />
                                    <th style={{ width: 40 }}>
                                        <Form.Check
                                            type="checkbox"
                                            checked={allSelectedOnPage}
                                            onChange={toggleAll}
                                            data-testid="select-all-checkbox"
                                        />
                                    </th>
                                    <th style={{ width: 56 }}>#</th>
                                    <th>Email</th>
                                    <th>Role</th>
                                    <th>Status</th>
                                    <th className="text-end" style={{ width: 100 }}>Actions</th>
                                </tr>
                            </thead>

                            <tbody className="table-group-divider">
                                {/* skeleton rows */}
                                {loading && Array.from({ length: 6 }).map((_, i) => (
                                    <tr key={`ph-${i}`} data-testid={`skeleton-row-${i}`}>
                                        <td />
                                        <td><Form.Check disabled data-testid={`skeleton-checkbox-${i}`} /></td>
                                        <td className="text-muted"><Placeholder xs={4} /></td>
                                        <td><Placeholder as="div" animation="glow"><Placeholder xs={8} /></Placeholder></td>
                                        <td className="text-muted"><Placeholder xs={6} /></td>
                                        <td><Placeholder xs={4} /></td>
                                        <td className="text-end"><Placeholder xs={3} /></td>
                                    </tr>
                                ))}

                                {!loading && data.length === 0 && (
                                    <tr data-testid="no-invitations-row">
                                        <td colSpan={7}>
                                            <NoResult
                                                line_1="No invitations found"
                                                line_2="Try adjusting filters or send a new invitation."
                                            />
                                        </td>
                                    </tr>
                                )}

                                {!loading && data.map((r: typeof data[number], i: number) => {
                                    const id = Number(r.invitation_id);
                                    const rowNumber = start + i;
                                    const isExpanded = expandedIds.has(id);
                                    const detailId = `invitation-detail-${id}`;

                                    return [
                                        <tr key={id} data-testid={`invitation-row-${id}`}>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="btn btn-link btn-sm p-0 border-0 text-muted"
                                                    aria-expanded={isExpanded}
                                                    aria-controls={detailId}
                                                    aria-label={isExpanded ? "Collapse details" : "Expand details"}
                                                    onClick={() => toggleExpanded(id)}
                                                    data-testid={`expand-btn-${id}`}
                                                >
                                                    {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
                                                </button>
                                            </td>
                                            <td>
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={selectedHas(id)}
                                                    onChange={(e) => toggleOne(id, e.target.checked)}
                                                    data-testid={`select-invitation-checkbox-${id}`}
                                                />
                                            </td>
                                            <td className="text-muted">{rowNumber}</td>

                                            <td className="fw-semibold">
                                                <span
                                                    className="link-body-emphasis"
                                                    data-testid={`invitation-email-${id}`}
                                                >
                                                    {r.invitee_email}
                                                </span>
                                            </td>
                                            <td className="text-muted" data-testid={`invitation-role-${id}`}>{r.profile_description || "—"}</td>

                                            <td>
                                                <StatusBadge
                                                    status={r.invite_status_name.toLowerCase() as InvitationStatus}
                                                    data-testid={`invitation-status-${id}`}
                                                />
                                            </td>

                                            <td className="text-end">
                                                {renderInviteActions(r)}
                                            </td>
                                        </tr>,

                                        isExpanded && (
                                            <tr key={detailId} className="ntcog-row-detail" data-testid={detailId}>
                                                <td />
                                                <td colSpan={6}>
                                                    <div className="px-2 py-2">
                                                        <div className="row g-2 small">
                                                            <div className="col-12 col-md-6 col-lg-4">
                                                                <span className="text-muted">Invited by:</span> {r.invited_by_name || "—"}
                                                            </div>
                                                            <div className="col-12 col-md-6 col-lg-4">
                                                                <span className="text-muted">Department:</span> {r.department_name || "—"}
                                                            </div>
                                                            <div className="col-12 col-md-6 col-lg-4">
                                                                <span className="text-muted">Scope:</span> {r.scope_type || "—"}{r.scope_ref_name ? ` · ${r.scope_ref_name}` : ""}
                                                            </div>
                                                            <div className="col-12 col-md-6 col-lg-4">
                                                                <span className="text-muted">Role:</span> {r.profile_description || "—"}
                                                            </div>
                                                            <div className="col-12 col-md-6 col-lg-4">
                                                                <span className="text-muted">Created:</span> {formateDate(r.created_at)}
                                                            </div>
                                                            <div className="col-12 col-md-6 col-lg-4">
                                                                <span className="text-muted">Expires:</span> {formateDate(r.expires_at)}
                                                            </div>
                                                            <div className="col-12 col-md-6 col-lg-4">
                                                                <span className="text-muted">TRN:</span> {r.invitee_trn || "—"}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ),
                                    ];
                                })}
                            </tbody>
                        </Table>
                    </div>

                    {/* Mobile (< md): stacked cards. The select checkbox lives in the card
                        header (feeds bulk actions); the expand-row fields show inline as details. */}
                    <div className="ntcog-card-list d-md-none" data-testid="invitations-cards">
                        {loading && Array.from({ length: 6 }).map((_, i) => (
                            <div key={`ph-card-${i}`} className="ntcog-card-list__item">
                                <EntityCard
                                    testId={`invitation-card-skeleton-${i}`}
                                    title={<Placeholder as="span" animation="glow"><Placeholder xs={6} /></Placeholder>}
                                    description={<Placeholder as="span" animation="glow"><Placeholder xs={8} /></Placeholder>}
                                />
                            </div>
                        ))}
                        {!loading && data.length === 0 && (
                            <NoResult
                                line_1="No invitations found"
                                line_2="Try adjusting filters or send a new invitation."
                            />
                        )}
                        {!loading && data.map((r: typeof data[number]) => {
                            const id = Number(r.invitation_id);
                            return (
                                <div key={id} className="ntcog-card-list__item">
                                    <EntityCard
                                        testId={`invitation-card-${id}`}
                                        ariaLabel={`Invitation ${r.invitee_email}`}
                                        title={
                                            <div className="d-flex align-items-center gap-2">
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={selectedHas(id)}
                                                    onChange={(e) => toggleOne(id, e.target.checked)}
                                                    aria-label={`Select invitation ${r.invitee_email}`}
                                                    data-testid={`select-invitation-checkbox-${id}`}
                                                />
                                                <span className="link-body-emphasis text-break">{r.invitee_email}</span>
                                            </div>
                                        }
                                        status={
                                            <StatusBadge
                                                status={r.invite_status_name.toLowerCase() as InvitationStatus}
                                                data-testid={`invitation-status-${id}`}
                                            />
                                        }
                                        actions={renderInviteActions(r)}
                                        details={[
                                            { label: "Role", value: r.profile_description || "—" },
                                            { label: "Department", value: r.department_name || "—" },
                                            { label: "Scope", value: `${r.scope_type || "—"}${r.scope_ref_name ? ` · ${r.scope_ref_name}` : ""}` },
                                            { label: "Invited by", value: r.invited_by_name || "—" },
                                            { label: "Created", value: formateDate(r.created_at) },
                                            { label: "Expires", value: formateDate(r.expires_at) },
                                            { label: "TRN", value: r.invitee_trn || "—" },
                                        ]}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </Card.Body>
            </Card>

            {showPagination && (
                <TablePaginationFooter
                    className="mt-3"
                    testId="pagination-bar"
                    info={`Showing ${start} to ${end} of ${total} invitations`}
                >
                    <PaginationBar
                        page={pageNumber}
                        totalPages={last_page ?? totalPages}
                        onPage={(p) => setPage(p)}
                    />
                </TablePaginationFooter>
            )}

            {/* Confirm Cancel modal */}
            <Modal
                show={!!confirmState}
                onHide={closeConfirm}
                centered
                backdrop="static"
                keyboard={!confirming}
                data-testid="cancel-confirm-modal"
            >
                <Modal.Header closeButton={!confirming} data-testid="cancel-confirm-modal-header">
                    <Modal.Title>
                        {confirmState?.kind === "bulk"
                            ? "Cancel Invitations"
                            : "Cancel Invitation"}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body data-testid="cancel-confirm-modal-body">
                    {confirmState?.kind === "bulk" ? (
                        <>
                            Are you sure you want to cancel{" "}
                            <strong>{confirmState.ids.length}</strong>{" "}
                            invitation{confirmState.ids.length > 1 ? "s" : ""}? This action cannot
                            be undone.
                        </>
                    ) : (
                        <>
                            Are you sure you want to cancel the invitation for{" "}
                            <strong>{confirmState?.email ?? "this user"}</strong>? This action
                            cannot be undone.
                        </>
                    )}
                </Modal.Body>

                <Modal.Footer data-testid="cancel-confirm-modal-footer">
                    <Button
                        variant="outline-secondary"
                        onClick={closeConfirm}
                        disabled={confirming}
                        data-testid="cancel-confirm-no-btn"
                    >
                        No
                    </Button>
                    <Button
                        variant="danger"
                        onClick={confirmCancel}
                        disabled={confirming}
                        aria-busy={confirming}
                        data-testid="cancel-confirm-yes-btn"
                    >
                        {confirming ? (
                            <>
                                <Spinner size="sm" animation="border" className="me-2" aria-hidden="true" />
                                Cancelling…
                            </>
                        ) : (
                            "Yes, cancel"
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default InvitationsTable;
